import React, { useEffect, useRef, useState } from "react";
import { Bot, Send, Sparkles, X } from "lucide-react";

type CompanionMessage = {
  role: "user" | "assistant";
  content: string;
};

function renderInlineCompanionText(text: string) {
  return text.split(/(\*\*[^*]+\*\*)/g).map((part, index) => {
    if (part.startsWith("**") && part.endsWith("**") && part.length > 4) {
      return <strong key={index}>{part.slice(2, -2)}</strong>;
    }
    return <React.Fragment key={index}>{part}</React.Fragment>;
  });
}

function CompanionFormattedText({ content }: { content: string }) {
  const lines = content.split("\n");
  return (
    <div className="space-y-1.5">
      {lines.map((rawLine, index) => {
        const line = rawLine.trimEnd();
        if (!line.trim()) return <div key={index} className="h-1.5" />;

        if (line.startsWith("### ")) {
          return (
            <p key={index} className="pt-1 font-semibold">
              {renderInlineCompanionText(line.slice(4))}
            </p>
          );
        }

        if (line.startsWith("- ")) {
          return (
            <div key={index} className="flex gap-2">
              <span aria-hidden="true">•</span>
              <span>{renderInlineCompanionText(line.slice(2))}</span>
            </div>
          );
        }

        return <p key={index}>{renderInlineCompanionText(line)}</p>;
      })}
    </div>
  );
}

type CompanionContext =
  | {
      type: "advisor";
      recommendationIds: Array<string | number>;
    }
  | {
      type: "advisor_both";
      recommendationIds: Array<string | number>;
    }
  | {
      type: "session";
      sessionId: string | number;
    };

export function CompanionOverlay({
  context,
  externalQuestion,
}: {
  context: CompanionContext;
  externalQuestion?: { id: string; text: string } | null;
}) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<CompanionMessage[]>([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const scrollRef = useRef<HTMLDivElement>(null);
  const handledExternalQuestion = useRef<string | null>(null);

  useEffect(() => {
    if (!open) return;
    requestAnimationFrame(() => {
      scrollRef.current?.scrollTo({
        top: scrollRef.current.scrollHeight,
        behavior: "smooth",
      });
    });
  }, [open, messages, sending]);

  async function sendMessage(text?: string) {
    const message = (text ?? draft).trim();
    if (!message || sending) return;

    const previous = messages.slice(-8);
    setMessages((current) => [...current, { role: "user", content: message }]);
    setDraft("");
    setError("");
    setSending(true);

    try {
      const apiContext =
        context.type === "session"
          ? { type: "session", session_id: context.sessionId }
          : {
              type: context.type,
              recommendation_ids: context.recommendationIds,
            };

      const response = await fetch("/app-api/companion/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          message,
          messages: previous,
          context: apiContext,
        }),
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(
          data.error ||
            (response.status === 503
              ? "CatchCompass AI is not connected yet."
              : "The Companion could not answer that."),
        );
      }

      const answer = String(data.answer || "").trim();
      if (!answer) throw new Error("The Companion returned an empty answer.");

      setMessages((current) => [
        ...current,
        { role: "assistant", content: answer },
      ]);
    } catch (caught) {
      setError(
        caught instanceof Error
          ? caught.message
          : "The Companion could not answer that.",
      );
    } finally {
      setSending(false);
    }
  }

  useEffect(() => {
    if (
      !externalQuestion ||
      handledExternalQuestion.current === externalQuestion.id
    )
      return;
    handledExternalQuestion.current = externalQuestion.id;
    setOpen(true);
    void sendMessage(externalQuestion.text);
    // externalQuestion IDs are unique per deliberate angler action.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalQuestion?.id]);

  const starterPrompts =
    context.type === "session"
      ? [
          "What would you focus on right now?",
          "Explain what my session evidence is telling us.",
          "What should make me consider changing something?",
        ]
      : [
          "Explain this advice in plain English.",
          "What is the strongest evidence behind this plan?",
          "What would make you change this approach?",
        ];

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="fixed bottom-20 right-4 z-40 flex min-h-12 items-center gap-2 rounded-full border border-accent/40 bg-accent px-4 py-3 text-sm font-bold text-white shadow-xl md:bottom-6 md:right-6"
        aria-label="Ask CatchCompass"
      >
        <Sparkles size={18} />
        Ask CatchCompass
      </button>

      {open ? (
        <div className="fixed inset-0 z-[80] flex items-end bg-black/45 sm:items-center sm:justify-end sm:p-4">
          <section
            className="flex h-[82dvh] w-full flex-col rounded-t-2xl border border-border bg-background shadow-2xl sm:h-[min(760px,90dvh)] sm:max-w-md sm:rounded-2xl"
            role="dialog"
            aria-modal="true"
            aria-label="CatchCompass AI Companion"
          >
            <header className="flex items-start justify-between gap-3 border-b border-border p-4">
              <div className="flex min-w-0 items-start gap-3">
                <div className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-accent/10 text-accent">
                  <Bot size={20} />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-bold text-primary">
                    CatchCompass Companion
                  </p>
                  <p className="mt-0.5 text-xs leading-5 text-secondary">
                    Fishing-only · read-only · grounded in this screen's evidence
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="flex size-10 shrink-0 items-center justify-center rounded-lg border border-border text-secondary"
                aria-label="Close Companion"
              >
                <X size={18} />
              </button>
            </header>

            <div
              ref={scrollRef}
              className="min-h-0 flex-1 overflow-y-auto px-4 py-4"
            >
              {messages.length === 0 ? (
                <div className="space-y-4">
                  <div className="rounded-xl border border-border bg-raised p-4">
                    <p className="text-sm font-semibold text-primary">
                      Ask about the fishing in front of you.
                    </p>
                    <p className="mt-2 text-sm leading-6 text-secondary">
                      I can explain the Advisor, Scout evidence, current session,
                      catches and conditions. I can reason about something you tell
                      me here, but I will not silently write it into your fishing log.
                    </p>
                  </div>
                  <div className="space-y-2">
                    {starterPrompts.map((prompt) => (
                      <button
                        key={prompt}
                        type="button"
                        onClick={() => sendMessage(prompt)}
                        className="w-full rounded-xl border border-border bg-raised px-3 py-3 text-left text-sm font-medium text-primary hover:bg-inset"
                      >
                        {prompt}
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  {messages.map((message, index) => (
                    <div
                      key={index}
                      className={
                        message.role === "user"
                          ? "ml-8 rounded-2xl rounded-br-md bg-accent px-3 py-2.5 text-sm leading-6 text-white"
                          : "mr-4 rounded-2xl rounded-bl-md border border-border bg-raised px-3 py-2.5 text-sm leading-6 text-primary"
                      }
                    >
                      <CompanionFormattedText content={message.content} />
                    </div>
                  ))}
                  {sending ? (
                    <div className="mr-16 rounded-2xl rounded-bl-md border border-border bg-raised px-3 py-2.5 text-sm text-secondary">
                      Looking at the fishing evidence…
                    </div>
                  ) : null}
                </div>
              )}

              {error ? (
                <p className="mt-3 rounded-lg bg-error-weak px-3 py-2 text-sm text-error">
                  {error}
                </p>
              ) : null}
            </div>

            <footer className="border-t border-border bg-raised p-3">
              <div className="flex items-end gap-2">
                <textarea
                  value={draft}
                  onChange={(event) => setDraft(event.target.value)}
                  onKeyDown={(event) => {
                    if (
                      event.key === "Enter" &&
                      !event.shiftKey &&
                      !event.nativeEvent.isComposing
                    ) {
                      event.preventDefault();
                      sendMessage();
                    }
                  }}
                  rows={2}
                  maxLength={1200}
                  placeholder="Ask about your fishing…"
                  className="min-h-12 flex-1 resize-none rounded-xl border border-border bg-background px-3 py-2.5 text-sm text-primary placeholder:text-tertiary focus:border-accent focus:outline-none"
                />
                <button
                  type="button"
                  onClick={() => sendMessage()}
                  disabled={!draft.trim() || sending}
                  className="flex size-12 shrink-0 items-center justify-center rounded-xl bg-accent text-white disabled:opacity-50"
                  aria-label="Send to CatchCompass Companion"
                >
                  <Send size={18} />
                </button>
              </div>
              <p className="mt-2 text-[11px] leading-4 text-tertiary">
                Relevant fishing context is sent to the AI provider for this reply.
                CatchCompass does not write this chat into catches, sessions, Scout
                observations or fishing evidence.
              </p>
            </footer>
          </section>
        </div>
      ) : null}
    </>
  );
}
